import { FC, useState } from 'react';

import { Drawer, Stack, TextField } from '@mui/material';

import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';

import tassAxios from '@/apis/tass-api/axios';

type AddUserSidepanelProps = {
  onCancel: () => void;
  onSubmit: (email: string) => void;
};

const MAX_EMAIL_LENGTH = 254;

const isValidEmail = (email: string): boolean => {
  if (email.length === 0 || email.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  if (/\s/.test(email)) {
    return false;
  }

  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || atIndex !== email.lastIndexOf('@')) {
    return false;
  }

  const domain = email.slice(atIndex + 1);
  const labels = domain.split('.');
  return labels.length >= 2 && labels.every((label) => label.length > 0);
};

export const AddUserSidepanel: FC<AddUserSidepanelProps> = ({ onCancel, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isValidating, setIsValidating] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Not a valid email address');
      return;
    }

    setIsValidating(true);
    try {
      const response = await tassAxios.get('/api/Admin/GetUserByEmail', {
        params: { email },
      });
      if (response.data) {
        setError('User with this email already exists');
        setIsValidating(false);
        return;
      }
    } catch {
      // 404 means user doesn't exist — that's what we want
    }
    setIsValidating(false);
    onSubmit(email.trim());
  };

  return (
    <Drawer open anchor="right">
      <div
        style={{
          display: 'flex',
          height: '100%',
          width: 400,
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 20,
          background: '#2c2c2c',
        }}>
        <Stack flex="auto 1 1" spacing="20px">
          <p style={{ fontWeight: '600', margin: '8px 0 12px' }}>New SDK user</p>
          <TextField
            required
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(undefined);
            }}
            error={!!error}
            helperText={error}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            sx={{
              'input, label, fieldset': { fontSize: 14 },
              input: { padding: '13px 14px' },
              label: { marginTop: '-3px' },
            }}
          />
        </Stack>
        <div style={{ height: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SecondaryButton width={150} label="Cancel" onClick={onCancel} />
          <PrimaryButton width={150} label="Create" disabled={isValidating} onClick={handleSubmit} />
        </div>
      </div>
    </Drawer>
  );
};
